import { useState } from 'react'
import { Button } from '@/ui/components/'
import { Badge } from '@/ui/components/'
import { Sparkles } from '@/ui/icons'
import type { FeedCard as FeedCardType } from '@/shared/api'
import { useTrips } from '@/modules/trips/hooks/useTrips'
import { FeedRow } from './FeedRow'
import { RANGER_ICON_CHIP } from './AiRecommendationCard'

interface WeekendSuggestionCardProps {
  card: FeedCardType
}

function formatDateRange(start?: string, end?: string): string {
  if (!start) return ''
  const fmt = (d: string) => {
    const [y, m, day] = d.slice(0, 10).split('-').map(Number)
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const startStr = fmt(start)
  if (!end || end.slice(0, 10) === start.slice(0, 10)) return startStr
  return `${startStr} – ${fmt(end)}`
}

/** A weekly, AI-curated weekend trip suggestion. "Plan this trip" turns it into a real trip via the existing quick-create flow — nothing is created until clicked. */
export function WeekendSuggestionCard({ card }: WeekendSuggestionCardProps) {
  const { createQuickTrip } = useTrips()
  const [isCreating, setIsCreating] = useState(false)

  const meta = card.metadata ?? {}
  const campsiteName = typeof meta.campsiteName === 'string' ? meta.campsiteName : undefined
  const startDate = typeof meta.startDate === 'string' ? meta.startDate : undefined
  const endDate = typeof meta.endDate === 'string' ? meta.endDate : undefined
  const weatherSummary = typeof meta.weatherSummary === 'string' ? meta.weatherSummary : undefined
  const campsiteLatitude = typeof meta.campsiteLatitude === 'number' ? meta.campsiteLatitude : undefined
  const campsiteLongitude = typeof meta.campsiteLongitude === 'number' ? meta.campsiteLongitude : undefined
  const suggestionId = typeof meta.suggestionId === 'string' ? meta.suggestionId : undefined

  const dateRange = formatDateRange(startDate, endDate)
  const subtitle = (
    <>
      {[campsiteName, dateRange, weatherSummary].filter(Boolean).join(' · ')}
      {(campsiteName || dateRange || weatherSummary) && ' · '}
      {card.body}
    </>
  )

  const handlePlanTrip = async () => {
    if (campsiteLatitude == null || campsiteLongitude == null) return
    setIsCreating(true)
    try {
      await createQuickTrip({
        name: card.title,
        campspot_latitude: campsiteLatitude,
        campspot_longitude: campsiteLongitude,
        campspot_name: campsiteName,
        start_date: startDate,
        end_date: endDate,
        description: card.body,
        suggestion_id: suggestionId,
      })
    } catch {
      setIsCreating(false)
    }
  }

  return (
    <FeedRow
      title={card.title}
      subtitle={subtitle}
      icon={Sparkles}
      iconChipClassName={RANGER_ICON_CHIP}
      badge={
        <Badge variant="secondary" className="bg-adventure-100 text-adventure-700 dark:bg-adventure-900/40 dark:text-adventure-300">
          Ranger
        </Badge>
      }
      action={
        <Button size="sm" onClick={handlePlanTrip} disabled={isCreating}>
          {isCreating ? 'Planning…' : 'Plan this trip'}
        </Button>
      }
    />
  )
}
